import { cn } from '@hs/ui';
import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';

import { isCountdownActive } from './countdownUtils';
import { CutdownCardUnit } from './CutdownCardUnit';
import {
  CUTDOWN_CARD_UNIT_LABELS,
  DEFAULT_CUTDOWN_CARD_UNITS,
  type CutdownCardProps,
  type CutdownCardTimeUnit as UnitKey,
} from './types';
import { useCountdownRemaining } from './useCountdownRemaining';

function resolveZeroPadLength(zeroPad: CutdownCardProps['zeroPad']): number {
  if (zeroPad === true) return 2;
  if (typeof zeroPad === 'number') return zeroPad;
  return 0;
}

function formatDisplayValue(value: number, zeroPadLength: number): string {
  if (zeroPadLength <= 0) return String(value);
  return String(value).padStart(zeroPadLength, '0');
}

const ZERO_VALUES: Record<UnitKey, number> = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
};

export const CutdownCard: FC<CutdownCardProps> = ({
  date,
  layout = 'horizontal',
  units = DEFAULT_CUTDOWN_CARD_UNITS,
  animate = true,
  zeroPad = false,
  borderRadius = 0,
  backgroundColor = '#ebebeb',
  border = '1px solid #404040',
  numberColor = '#000000',
  labelColor = '#000000',
  cardSize = 72,
  gap = 8,
  scale = 1,
  className,
  style,
  completedContent,
  onComplete,
}) => {
  const visibleUnits = useMemo(
    () => units.filter((u): u is UnitKey => DEFAULT_CUTDOWN_CARD_UNITS.includes(u)),
    [units],
  );

  const zeroPadLength = resolveZeroPadLength(zeroPad);
  const countdownActive = isCountdownActive(date);
  const remaining = useCountdownRemaining(countdownActive ? date : null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const completedFiredRef = useRef(false);

  useEffect(() => {
    if (!remaining?.completed) {
      completedFiredRef.current = false;
      return;
    }
    if (completedFiredRef.current) return;
    completedFiredRef.current = true;
    onCompleteRef.current?.();
  }, [remaining?.completed]);

  const rootStyle = useMemo(() => {
    const scaleTransform = scale !== 1 ? `scale(${scale})` : undefined;
    const transform = [scaleTransform, style?.transform].filter(Boolean).join(' ') || undefined;
    return {
      ...style,
      gap,
      transform,
      transformOrigin:
        style?.transformOrigin ??
        (layout === 'horizontal' ? 'left center' : 'top center'),
    };
  }, [style, gap, scale, layout]);

  const renderUnits = (
    values: Record<UnitKey, number>,
    options: { animateDigits: boolean; completed: boolean; completedSlot?: ReactNode },
  ) => {
    if (options.completed && options.completedSlot != null) {
      return (
        <div className={cn('inline-flex', className)} style={rootStyle}>
          {options.completedSlot}
        </div>
      );
    }

    const displayValues: Record<UnitKey, string> = {
      days: formatDisplayValue(values.days, zeroPadLength),
      hours: formatDisplayValue(values.hours, zeroPadLength),
      minutes: formatDisplayValue(values.minutes, zeroPadLength),
      seconds: formatDisplayValue(values.seconds, zeroPadLength),
    };

    return (
      <div
        className={cn(
          'inline-flex',
          layout === 'horizontal' ? 'flex-row' : 'flex-col',
          className,
        )}
        style={rootStyle}
        role="timer"
        aria-live={countdownActive ? 'polite' : 'off'}
      >
        {visibleUnits.map((unit) => (
          <CutdownCardUnit
            key={unit}
            display={displayValues[unit]}
            label={CUTDOWN_CARD_UNIT_LABELS[unit]}
            animate={options.animateDigits && !options.completed}
            cardSize={cardSize}
            borderRadius={borderRadius}
            backgroundColor={backgroundColor}
            border={border}
            numberColor={numberColor}
            labelColor={labelColor}
          />
        ))}
      </div>
    );
  };

  if (!countdownActive || !remaining) {
    return renderUnits(ZERO_VALUES, { animateDigits: false, completed: true });
  }

  return renderUnits(
    {
      days: remaining.days,
      hours: remaining.hours,
      minutes: remaining.minutes,
      seconds: remaining.seconds,
    },
    {
      animateDigits: animate,
      completed: remaining.completed,
      completedSlot: completedContent,
    },
  );
};
